import { PALETTE_CASES } from '../data/paletteCases';
import { STATIC_PAGES, SITE_URL, COLOR_HEXES, isIndexablePath } from '../data/siteRoutes';
import type { RGB } from '../types';
import { colorMap, rgbToHex, getNearestColorName, rgbToHsl } from './colors';
import { getContrastRatio } from './colorAnalysis';
import { getComplementaryColor } from './colorHarmony';
import { blogPosts } from '../data/blogPosts';
import { COLOR_COMPARISONS } from '../data/colorComparisons';
import { PALETTE_COLLECTIONS } from '../data/paletteCollections';

let activeRoute: { pathname: string; search: string } | undefined;

// Page effects run after RouteMetadata. Keep their URL signals on the same policy.
const resolveMetadataUrl = (href: string) => {
  const url = new URL(href, SITE_URL);
  return activeRoute && url.origin === SITE_URL && url.pathname === activeRoute.pathname
    ? getRouteUrlMetadata(activeRoute.pathname, activeRoute.search).canonical
    : href;
};

// ─── Shared module-level helpers ────────────────────────────────────────────

const setMetaName = (name: string, content: string) => {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setMetaProp = (property: string, content: string) => {
  if (property === 'og:url') content = resolveMetadataUrl(content);
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href: string) => {
  href = resolveMetadataUrl(href);
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setHreflang = (canonicalUrl: string) => {
  canonicalUrl = resolveMetadataUrl(canonicalUrl);
  let el = document.querySelector('link[rel="alternate"][hreflang="en"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', 'en');
    document.head.appendChild(el);
  }
  el.setAttribute('href', canonicalUrl);

  let xDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]') as HTMLLinkElement | null;
  if (!xDefault) {
    xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    document.head.appendChild(xDefault);
  }
  xDefault.setAttribute('href', canonicalUrl);
};

const setJsonLdById = (id: string, schema: object) => {
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.setAttribute('type', 'application/ld+json');
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(schema);
};

const BASE_URL = 'https://color-match.io';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-default.png`;

const setOgImage = (imageUrl: string = DEFAULT_OG_IMAGE) => {
  setMetaProp('og:image', imageUrl);
  setMetaName('twitter:image', imageUrl);
};

// ─── Color Explorer Page (/color/:hex) ──────────────────────────────────────

export const updateColorDetailMetadata = (hex: string, name: string, rgb: RGB) => {
  if (typeof document === 'undefined') return;

  try {
    const cleanHex = hex.replace('#', '').toUpperCase();
    const canonicalUrl = `${BASE_URL}/color/${cleanHex}`;
    const namedRgb = colorMap[name];
    const exactName = namedRgb?.every((channel, index) => channel === [rgb.r, rgb.g, rgb.b][index]);

    // Compute derived values for rich meta
    const comp = getComplementaryColor(rgb);
    const compHex = rgbToHex(comp.r, comp.g, comp.b);
    const compName = getNearestColorName(comp.r, comp.g, comp.b);
    const whiteContrast = getContrastRatio(hex, '#FFFFFF');
    const blackContrast = getContrastRatio(hex, '#000000');
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    const achromatic = rgb.r === rgb.g && rgb.g === rgb.b;
    const hslDescription = `HSL(${achromatic ? 'none' : Math.round(hsl.h) + '°'}, ${Math.round(hsl.s)}%, ${Math.round(hsl.l)}%)`;
    const harmonyDescription = achromatic
      ? 'This achromatic color has no distinct HSL complementary hue.'
      : `HSL complementary color: ${compHex} (nearest name: ${compName}).`;

    const title = `${exactName ? name : 'Near ' + name} Color: ${hex} HEX, RGB & Palettes | Color Match`;
    const description = `${exactName ? name + ' is ' + hex : hex + ' (near ' + name + ')'}, RGB(${rgb.r}, ${rgb.g}, ${rgb.b}). Contrast: ${whiteContrast.toFixed(2)}:1 on white, ${blackContrast.toFixed(2)}:1 on black. Explore palettes, tints and shades.`;

    document.title = title;
    setCanonical(canonicalUrl);
    setHreflang(canonicalUrl);

    setMetaName('description', description);
    setMetaName('keywords', `${name}, ${hex}, complementary color, contrast ratio, color palette, hex to rgb, color harmony, WCAG accessibility, ${compName}`);
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', title);
    setMetaName('twitter:description', description);

    setMetaProp('og:title', title);
    setMetaProp('og:description', description);
    setMetaProp('og:type', 'website');
    setMetaProp('og:url', canonicalUrl);
    setOgImage(COLOR_HEXES.includes(cleanHex) ? `${BASE_URL}/og/${cleanHex}.png` : DEFAULT_OG_IMAGE);


    setJsonLdById('page-jsonld', {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'SoftwareApplication',
          name: 'Color Match',
          applicationCategory: 'DesignApplication',
          operatingSystem: 'Web',
          url: BASE_URL,
          description: 'Free color explorer for designers and developers. Check contrast ratios, generate harmonies, and export CSS/Tailwind tokens.',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          featureList: [
            'Color Explorer',
            'Color Converter',
            'WCAG Contrast Checker',
            'APCA Contrast Checker',
            'Color Harmony Generator',
            'Color Blindness Simulator',
            'CSS/Tailwind/SCSS Export',
            'Design System Token Generator',
          ],
        },
        {
          '@type': exactName ? 'DefinedTerm' : 'Thing',
          name: exactName ? name : `${hex} (nearest reference: ${name})`,
          description: `${exactName ? 'Named color reference' : 'Custom color; ' + name + ' is its nearest reference name'} with hex code ${hex}, RGB(${rgb.r}, ${rgb.g}, ${rgb.b}), ${hslDescription}. ${harmonyDescription}`,
          url: canonicalUrl,
          ...(exactName ? { inDefinedTermSet: {
            '@type': 'DefinedTermSet',
            name: 'Named Color References',
            url: `${BASE_URL}/colors`,
          } } : {}),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
            { '@type': 'ListItem', position: 2, name: 'Color names', item: `${BASE_URL}/colors` },
            { '@type': 'ListItem', position: 3, name, item: canonicalUrl },
          ],
        },
      ],
    });
  } catch (e) {
    console.warn('Failed to update detail metadata:', e);
  }
};

// ─── Blog Post Page (/blog/:slug) ────────────────────────────────────────────

export interface BlogPostMeta {
  title: string;
  excerpt: string;
  date: string;
  author: string;
  slug: string;
  tags?: string[];
  readingTime?: number;
  updated?: string;
  sources?: { text: string; href: string }[];
}

export const updateBlogPostMetadata = (post: BlogPostMeta): void => {
  try {
    const canonicalUrl = `${BASE_URL}/blog/${post.slug}`;
    const pageTitle = `${post.title} — Color Match Blog`;
    const keywords = post.tags
      ? [...post.tags, 'color design', 'accessibility', 'Color Match'].join(', ')
      : 'color design, accessibility, Color Match';

    document.title = pageTitle;
    setCanonical(canonicalUrl);
    setHreflang(canonicalUrl);

    setMetaName('description', post.excerpt);
    setMetaName('keywords', keywords);
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', pageTitle);
    setMetaName('twitter:description', post.excerpt);

    setMetaProp('og:title', pageTitle);
    setMetaProp('og:description', post.excerpt);
    setMetaProp('og:url', canonicalUrl);
    setMetaProp('og:type', 'article');
    setMetaProp('article:published_time', post.date);
    if (post.updated) setMetaProp('article:modified_time', post.updated);
    setMetaProp('article:author', post.author);
    setOgImage(`${BASE_URL}/og/blog/${post.slug}.png`);

    setJsonLdById('blog-post-jsonld', {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: post.title,
          description: post.excerpt,
          datePublished: post.date,
          dateModified: post.updated || post.date,
          ...(post.sources ? { citation: post.sources.map(source => source.href) } : {}),
          image: `${BASE_URL}/og/blog/${post.slug}.png`,
          inLanguage: 'en',
          keywords: post.tags || [],
          author: { '@type': 'Organization', name: post.author, url: BASE_URL },
          publisher: {
            '@type': 'Organization',
            name: 'Color Match',
            url: BASE_URL,
            logo: { '@type': 'ImageObject', url: DEFAULT_OG_IMAGE },
          },
          mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
          ...(post.readingTime ? { timeRequired: `PT${post.readingTime}M` } : {}),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: `${BASE_URL}/blog` },
            { '@type': 'ListItem', position: 3, name: post.title, item: canonicalUrl },
          ],
        },
      ],
    });
  } catch (e) {
    console.warn('Failed to update blog post metadata:', e);
  }
};

// ─── Blog Index Page (/blog) ─────────────────────────────────────────────────

export const updateBlogIndexMetadata = (): void => {
  try {
    const title = 'Blog — Color Theory, Accessibility & Design | Color Match';
    const description = 'Articles on WCAG accessibility, color theory, design systems, and the science of color — written for designers and developers.';
    const canonicalUrl = `${BASE_URL}/blog`;

    document.title = title;
    setCanonical(canonicalUrl);
    setHreflang(canonicalUrl);

    setMetaName('description', description);
    setMetaName('keywords', 'color blog, design accessibility, WCAG tutorial, color theory articles, color design');
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', title);
    setMetaName('twitter:description', description);

    setMetaProp('og:title', title);
    setMetaProp('og:description', description);
    setMetaProp('og:url', canonicalUrl);
    setMetaProp('og:type', 'website');
    setOgImage();

    setJsonLdById('blog-index-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Color Match Blog',
      description,
      url: canonicalUrl,
      hasPart: blogPosts.map(post => ({
        '@type': 'Article',
        headline: post.title,
        url: `${BASE_URL}/blog/${post.slug}`,
        datePublished: post.date,
        description: post.excerpt,
      })),
    });
  } catch (e) {
    console.warn('Failed to update blog index metadata:', e);
  }
};

// ─── About Page (/about) ─────────────────────────────────────────────────────

export const updateAboutMetadata = (): void => {
  try {
    const title = 'About Color Match — Free Color Tool for Designers & Developers';
    const description = 'Color Match is a free, professional-grade color analysis tool. Compare colors, check WCAG contrast ratios, generate harmonies, and export CSS/Tailwind values instantly.';
    const canonicalUrl = `${BASE_URL}/about`;

    document.title = title;
    setCanonical(canonicalUrl);
    setHreflang(canonicalUrl);

    setMetaName('description', description);
    setMetaName('keywords', 'color match, about, color tool, WCAG, contrast checker, color palette generator, hex to rgb');
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', title);
    setMetaName('twitter:description', description);

    setMetaProp('og:title', title);
    setMetaProp('og:description', description);
    setMetaProp('og:url', canonicalUrl);
    setMetaProp('og:type', 'website');
    setOgImage();

    setJsonLdById('page-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description,
      url: canonicalUrl,
      isPartOf: { '@type': 'WebSite', name: 'Color Match', url: BASE_URL },
    });
  } catch (e) {
    console.warn('Failed to update about metadata:', e);
  }
};

// ─── Color Theory Page (/theory) ─────────────────────────────────────────────

export const updateColorTheoryMetadata = (): void => {
  try {
    const title = 'Color Theory Guide — Harmonies, Wheels & Principles | Color Match';
    const description = 'Learn color theory fundamentals: the color wheel, harmony types (complementary, triadic, analogous), and how to apply them in UI design and branding.';
    const canonicalUrl = `${BASE_URL}/theory`;

    document.title = title;
    setCanonical(canonicalUrl);
    setHreflang(canonicalUrl);

    setMetaName('description', description);
    setMetaName('keywords', 'color theory, color wheel, complementary colors, analogous colors, color harmony, triadic colors, color design');
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', title);
    setMetaName('twitter:description', description);

    setMetaProp('og:title', title);
    setMetaProp('og:description', description);
    setMetaProp('og:url', canonicalUrl);
    setMetaProp('og:type', 'article');
    setOgImage();

    setJsonLdById('page-jsonld', {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: title,
          description,
          url: canonicalUrl,
          inLanguage: 'en',
          publisher: { '@type': 'Organization', name: 'Color Match', url: BASE_URL },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
            { '@type': 'ListItem', position: 2, name: 'Color Theory', item: canonicalUrl },
          ],
        },
      ],
    });
  } catch (e) {
    console.warn('Failed to update color theory metadata:', e);
  }
};

// ─── Color Models Page (/models) ─────────────────────────────────────────────

export const updateColorModelsMetadata = (): void => {
  try {
    const title = 'Color Models Explained — RGB, HSL, CMYK, HSV, OKLCH | Color Match';
    const description = 'Understand the differences between RGB, HSL, CMYK, HSV, and OKLCH color models. Learn when to use each model in web, print, and design tool workflows.';
    const canonicalUrl = `${BASE_URL}/models`;

    document.title = title;
    setCanonical(canonicalUrl);
    setHreflang(canonicalUrl);

    setMetaName('description', description);
    setMetaName('keywords', 'RGB color model, HSL, CMYK, HSV, OKLCH, color space conversion, hex color, web color, print color, hex to rgb, rgb to hsl');
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', title);
    setMetaName('twitter:description', description);

    setMetaProp('og:title', title);
    setMetaProp('og:description', description);
    setMetaProp('og:url', canonicalUrl);
    setMetaProp('og:type', 'article');
    setOgImage();

    setJsonLdById('page-jsonld', {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: title,
          description,
          url: canonicalUrl,
          inLanguage: 'en',
          publisher: { '@type': 'Organization', name: 'Color Match', url: BASE_URL },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
            { '@type': 'ListItem', position: 2, name: 'Color Models', item: canonicalUrl },
          ],
        },
      ],
    });
  } catch (e) {
    console.warn('Failed to update color models metadata:', e);
  }
};

// ─── Contact Page (/contact) ─────────────────────────────────────────────────

export const updateContactMetadata = (): void => {
  try {
    const title = 'Contact Us — Color Match';
    const description = 'Get in touch with the Color Match team. Send feedback, report a bug, or ask a question about our color comparison and accessibility tools.';
    const canonicalUrl = `${BASE_URL}/contact`;

    document.title = title;
    setCanonical(canonicalUrl);
    setHreflang(canonicalUrl);

    setMetaName('description', description);
    setMetaName('twitter:card', 'summary');
    setMetaName('twitter:title', title);
    setMetaName('twitter:description', description);

    setMetaProp('og:title', title);
    setMetaProp('og:description', description);
    setMetaProp('og:url', canonicalUrl);
    setMetaProp('og:type', 'website');
    setOgImage();

    setJsonLdById('page-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: title,
      description,
      url: canonicalUrl,
    });
  } catch (e) {
    console.warn('Failed to update contact metadata:', e);
  }
};

// ─── Privacy Policy (/privacy) ───────────────────────────────────────────────

export const updatePrivacyMetadata = (): void => {
  try {
    const title = 'Privacy Policy — Color Match';
    const description = 'Read how Color Match processes color tool inputs, contact inquiries, and your analytics and advertising choices.';
    const canonicalUrl = `${BASE_URL}/privacy`;

    document.title = title;
    setCanonical(canonicalUrl);
    setMetaName('description', description);
    setMetaName('robots', 'noindex, follow');

    setMetaProp('og:title', title);
    setMetaProp('og:description', description);
    setMetaProp('og:url', canonicalUrl);
    setMetaProp('og:type', 'website');
    setOgImage();
  } catch (e) {
    console.warn('Failed to update privacy metadata:', e);
  }
};

// ─── Terms of Service (/terms) ───────────────────────────────────────────────

export const updateTermsMetadata = (): void => {
  try {
    const title = 'Terms of Service — Color Match';
    const description = 'Review the Terms of Service for Color Match, including use conditions for the service and its outputs.';
    const canonicalUrl = `${BASE_URL}/terms`;

    document.title = title;
    setCanonical(canonicalUrl);
    setMetaName('description', description);
    setMetaName('robots', 'noindex, follow');

    setMetaProp('og:title', title);
    setMetaProp('og:description', description);
    setMetaProp('og:url', canonicalUrl);
    setMetaProp('og:type', 'website');
    setOgImage();
  } catch (e) {
    console.warn('Failed to update terms metadata:', e);
  }
};

// ─── Palette Page (/palette/:colors) ─────────────────────────────────────────

export const updatePaletteMetadata = (colors: string[]): void => {
  try {
    const hexList = colors.map(c => c.replace('#', '').toUpperCase());
    const colorParam = hexList.join('-');
    const canonicalUrl = `${BASE_URL}/palette/${colorParam}`;

    const colorNames = hexList.slice(0, 3).map(h => {
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      return getNearestColorName(r, g, b);
    });
    const namePreview = colorNames.join(', ');

    const example = PALETTE_CASES.find(item => item.colors === hexList.join('-'));
    const title = `${example?.title || namePreview} Palette — Color Match`;
    const description = example?.note || `Inspect this ${colors.length}-color palette: #${hexList.join(', #')}. Assign roles, check contrast pairs and copy CSS or edit in Studio.`;

    document.title = title;
    setCanonical(canonicalUrl);
    setHreflang(canonicalUrl);

    setMetaName('description', description);
    setMetaName('keywords', `color palette, ${namePreview}, hex colors, CSS variables, Tailwind colors`);
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', title);
    setMetaName('twitter:description', description);

    setMetaProp('og:title', title);
    setMetaProp('og:description', description);
    setMetaProp('og:url', canonicalUrl);
    setMetaProp('og:type', 'website');
    setOgImage();

    setJsonLdById('page-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: title,
      description,
      url: canonicalUrl,
      numberOfItems: colors.length,
      itemListElement: hexList.map((h, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: `#${h}`,
        url: `${BASE_URL}/color/${h}`,
      })),
    });
  } catch (e) {
    console.warn('Failed to update palette metadata:', e);
  }
};

// ─── Theme Palettes Page (/themes/:hex) ──────────────────────────────────────

export const updateThemePalettesMetadata = (hex: string, colorName: string): void => {
  try {
    const cleanHex = hex.replace('#', '').toUpperCase();
    const canonicalUrl = `${BASE_URL}/themes/${cleanHex}`;
    const title = `${colorName} Theme Palettes — 20 Color Schemes | Color Match`;
    const description = `Browse 20 generated theme palettes built around ${colorName} (#${cleanHex}). Inspect Pastel, Dark Mode and other starting points. Check role contrast before exporting.`;

    document.title = title;
    setCanonical(canonicalUrl);
    setHreflang(canonicalUrl);

    setMetaName('description', description);
    setMetaName('keywords', `${colorName} palette, theme palette, color scheme, pastel colors, dark mode palette, Tailwind theme`);
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', title);
    setMetaName('twitter:description', description);

    setMetaProp('og:title', title);
    setMetaProp('og:description', description);
    setMetaProp('og:url', canonicalUrl);
    setMetaProp('og:type', 'website');
    setOgImage();

    const themeNames = [
      'Pastel', 'Dark Mode', 'Vintage', 'Neon', 'Earth Tones',
      'Monochromatic', 'Jewel Tones', 'Nordic', 'Tropical', 'Art Deco',
      'Minimalist', 'Cyberpunk', 'Autumn', 'Ocean', 'Sunset',
      'Forest', 'Retro', 'Elegant', 'Playful', 'Corporate',
    ];

    setJsonLdById('page-jsonld', {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'ItemList',
          name: title,
          description,
          url: canonicalUrl,
          numberOfItems: 20,
          itemListElement: themeNames.map((theme, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: `${colorName} ${theme} Palette`,
          })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
            { '@type': 'ListItem', position: 2, name: `${colorName} Themes`, item: canonicalUrl },
          ],
        },
      ],
    });
  } catch (e) {
    console.warn('Failed to update theme palettes metadata:', e);
  }
};

const TRACKING_PARAMETERS = new Set([
  'gclid', 'dclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid',
  'twclid', 'li_fat_id', 'mc_cid', 'mc_eid', '_ga', '_gl',
]);

/** Acquisition tags do not create a different page; tool inputs still do. */
export function getRouteUrlMetadata(pathname: string, search = '') {
  const params = new URLSearchParams(search);
  for (const key of [...params.keys()]) {
    if (/^utm_/i.test(key) || TRACKING_PARAMETERS.has(key.toLowerCase())) params.delete(key);
  }
  params.sort();
  const query = params.toString();
  const knownPage = !pathname.startsWith('/blog/') || blogPosts.some(post => pathname === '/blog/' + post.slug);
  return {
    canonical: SITE_URL + pathname + (query ? '?' + query : ''),
    robots: isIndexablePath(pathname) && knownPage && !query
      ? 'index, follow, max-image-preview:large'
      : 'noindex, follow',
  };
}

/** Query-only navigation must preserve the current page's title and schemas. */
export function updateRouteUrlMetadata(pathname: string, search = '') {
  activeRoute = { pathname, search };
  const { canonical, robots } = getRouteUrlMetadata(pathname, search);
  setMetaName('robots', robots);
  setCanonical(canonical);
  setHreflang(canonical);
  setMetaProp('og:url', canonical);
}

/** Reapply URL signals after a page with its own metadata writer has mounted. */
export function syncRouteUrlMetadata() {
  if (activeRoute) updateRouteUrlMetadata(activeRoute.pathname, activeRoute.search);
}

/** Clear route-owned state before child page effects provide detailed metadata. */
export function resetRouteMetadata(pathname: string, search = '') {
  const page = STATIC_PAGES.find(p => p.path === pathname);
  document.querySelectorAll('script[type="application/ld+json"]').forEach(el => {
    if (!['org-jsonld', 'website-jsonld'].includes(el.id)) el.remove();
  });
  document.querySelectorAll('meta[property^="article:"],meta[name="keywords"]').forEach(el => el.remove());
  document.title = page?.title || 'Color Tools - Color Match';
  setMetaName('description', page?.description || 'Explore and compare color values with Color Match.');
  updateRouteUrlMetadata(pathname, search);
  setMetaProp('og:title', document.title);
  setMetaProp('og:description', page?.description || 'Explore and compare colors.');
  setMetaProp('og:type', 'website');
  setMetaName('twitter:card', 'summary_large_image');
  setMetaName('twitter:title', document.title);
  setMetaName('twitter:description', page?.description || 'Explore and compare colors.');
  const comparison = COLOR_COMPARISONS.find(item => pathname === '/compare/' + item.slug);
  const collection = PALETTE_COLLECTIONS.find(item => pathname === '/color-palettes/' + item.slug);
  setOgImage(comparison ? `${SITE_URL}/og/compare/${comparison.slug}.png` : collection ? `${SITE_URL}/og/palettes/${collection.slug}.png` : DEFAULT_OG_IMAGE);
  document.documentElement.dataset.route = pathname;
}
